/**
 * @file Represents the primary dashboard interface for the application.
 * The dashboard currently includes filtering capabilities, a search bar and reset actions.
 */
import { Box, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useContext } from 'react';
import ResetFiltersButton from './buttons/ResetFiltersButton';
import {
    FilterStatesContext,
    PEER_FILTER_ACTIONS
} from './contexts/filterStates';
import { isAnyFilterSet } from '../functions/filtering';
import FiltersGrid from './filters/FiltersGrid';
import { SearchBar } from './search/SearchBar';

/**
 * The `Dashboard` component renders the main dashboard of the application.
 * It currently provides a set of peer filters, a search bar and the capability to reset these.
 * This component is only displayed on medium or larger screens with enough space for all items in the Dashboard to display inline.
 *
 * @component
 * @returns {React.ReactElement} The rendered Dashboard component.
 */
const Dashboard = () => {
    const theme = useTheme();

    const filterState = useContext(FilterStatesContext);
    const resetVisible = isAnyFilterSet(filterState);

    return (
        <Grid id={'dashboardGrid'} container spacing={2} wrap='nowrap'>
            <Grid id={'dashboardSearchBar'} item md={3}>
                <SearchBar />
            </Grid>
            <Grid
                id={'dashboardFiltersGrid'}
                item
                md={10}
                data-analyticsid='filter-bar'
            >
                <Box
                    id={'dashboard'}
                    display='flex'
                    flexDirection='column'
                    borderRadius='100px'
                    bgcolor={theme.palette.background.panel}
                >
                    <Grid container wrap='nowrap' alignItems={'center'}>
                        <Grid item style={{ width: '100%' }}>
                            <FiltersGrid />
                        </Grid>
                        <Grid
                            item
                            sx={{
                                width: resetVisible ? '120px' : '0px',
                                transition: 'width 0.3s ease',
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                id='dashboardResetFiltersBox'
                                style={{
                                    height: '100%',
                                    paddingRight: theme.spacing(2),
                                    paddingLeft: theme.spacing(1)
                                }}
                            >
                                <ResetFiltersButton
                                    id={'dashboardResetFiltersButton'}
                                    onReset={() => {
                                        filterState.peerFilterDispatch({
                                            type: PEER_FILTER_ACTIONS.CLEAR_FILTERS
                                        });
                                        filterState.setSearchState(null);
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    );
};

export default Dashboard;
